'use client';
import React from 'react';
import { Form } from '../../../external/Form/Form';
import { Alert, Button, Grow, Stack, Typography } from '@mui/material';
import Link from 'next/link';
import { pageRoutes } from '../../../../network/pageRoutes/pageRoutes';
import { enqueueSnackbar } from 'notistack';
import { IForgotPasswordRequest, forgotPassword, forgotPasswordSchema } from '../../../../network/api/auth/forgotPassword/forgotPassword';
import FormInput from '../../../external/Form/FormInput/FormInput';
import { UseFormReturn, useFormContext } from 'react-hook-form';
import { CheckCircle } from '@mui/icons-material';

/**
 * Forgot Password page content
 * 
 * @returns Component
 */
export const ForgotPassword = () => {

    // HANDLERS

    /**
     * Handles submitting of the forgot password form to the forgot password API endpoint.
     * 
     * @param data Form Data to submit
     * @param formInstance reference to the current form instance
     * 
     * @returns void
     */
    const handleSubmit = async (data: IForgotPasswordRequest, formInstance?: UseFormReturn) => {

        const res = await forgotPassword(data);

        // Reset email sent
        if(res?.success) {
            enqueueSnackbar({
                message: 'Check your inbox for a link to reset your password',
                variant: 'info'
            });
            return
        }
       
       if(formInstance) {
        if(res?.errors?.details) {
            formInstance.setError('api', { message: res.errors.details as string, type: 'api' })
        }
       }
    }

    return (
        <Form 
            onSubmit={handleSubmit} 
            schema={forgotPasswordSchema}
        >
            <Typography variant='h4' mb={2}>FORGOT PASSWORD</Typography>
            <Typography variant='bodyGrey' display={'block'} mb={2}>
                Enter your email below to begin the password reset process.
            </Typography>
            <ForgotPasswordContent  />
        </Form>
    )

}

/**
 * Forgot password page form.
 * 
 * @returns Component
 */
const ForgotPasswordContent = () => {

    // FORM
    const { formState: { isSubmitSuccessful, isSubmitting, errors } } = useFormContext();
    
    return (
        <>
            <FormInput 
                label={'Email *'}
                name={'email'}
            />
            <Grow in={!!errors['api'] || isSubmitSuccessful}>
                <Alert 
                        severity={isSubmitSuccessful ? 'success' : 'error'}
                        variant='filled'
                    >
                        {   
                            isSubmitSuccessful ? 'Password reset email sent' :  
                            errors['api']?.message as string || 'An unknown error has occurred'
                        }
                </Alert>
            </Grow>
            <Stack gap={2} mt={3}>
                <Button startIcon={isSubmitSuccessful ? <CheckCircle sx={{ position: 'absolute', left: '6px', top: '6px', color: 'primary.light' }} /> : undefined} disabled={isSubmitting || isSubmitSuccessful} variant={'contained'} type='submit'>{isSubmitSuccessful ? 'Check your Inbox' : 'Send Reset Email'}</Button>
                <Link href={pageRoutes.auth.login}>
                    <Button color='secondary'>Back to Login</Button>
                </Link>
            </Stack>
        </>
      
    )

}